/* Wall Control - 3D product media slide (Sept 2026).
 *
 * <wc-model-3d> lives inside a gallery slide (snippets/product-3d-media.liquid).
 * It loads Google <model-viewer> (vendored: assets/model-viewer.min.js, with
 * the meshopt decoder in assets/meshopt_decoder.js), shows the theme-asset
 * GLB and recolours the model's powder-coat material(s) from the selected
 * variant's colour option, using the theme's swatch_config hex values.
 * Materials named "WC-Fixed-*" are never recoloured (wooden rods, the ghost
 * panels shown with the V-Bracket, and the fixed finishes of kits sold in
 * one colour), except "WC-Fixed-Cap", the rod's rubber end caps, which turn
 * white when the accessory colour is White and black otherwise. Kits carry
 * their steel accessories as "WC-PowderCoat-Accessory" and their plastic
 * bins as "WC-Bin": both follow the "Accessory Color" option (or the slide's
 * fixed data-accessory when the listing has no such option, else the colour
 * option), and the bins go clear on White (no white bins are made; the
 * white kits ship clear ones).
 *
 * Nothing is fetched until the shopper heads for the slide: the static
 * poster (assets/wc3d-<SKU>.webp, rendered in the default variant's colour)
 * fills the slide, the thumbnail and the lightbox, and the viewer boots when
 * the slide starts entering the viewport or the 3D thumbnail / slide is
 * pressed. A pointerdown / touchstart inside the gallery (the shopper is
 * about to swipe or pick a thumbnail) only *prefetches* the library.
 *
 * The theme re-renders the whole media gallery on every variant change, so
 * the live <model-viewer> node is cached on window and re-adopted by the
 * fresh slide instead of downloading and parsing the model again.
 *
 * AR "View in your room" (Sept 2026, testing): on phones and tablets only,
 * the viewer gets model-viewer's AR mode with wall placement at fixed
 * real-world scale (the GLBs are metres, front face on +Z). Android uses
 * WebXR in-page, so the recoloured material carries over; iOS gets a USDZ
 * exported on the device from the live scene (see the "WC patch" in
 * model-viewer.min.js, which adds vertical anchoring for the wall case).
 * Scene Viewer is deliberately left out: it re-downloads the raw GLB, so
 * the colour would be lost, and it does not decode meshopt. model-viewer
 * only shows the button when the device can actually start AR. Back from
 * AR, a line of copy invites the shopper to add the variant from the theme's
 * sticky add-to-cart bar (arCartNote). Quick Look's own "Add to cart" banner
 * was tried and dropped (Sept 2026): it needs a pre-built USDZ per colour and
 * replaces Quick Look's photo button - see 3D_ATTRIBUTION_PLAN.md 6.1.
 * Quick Look's share button shares the USDZ file. No URL fragment can change
 * that or add a banner here: model-viewer hands Quick Look the on-device
 * export as a blob: URL, and Safari drops fragments on blobs (model-viewer
 * discussions #4575 / #3811). So `src` stays the plain asset URL: a page
 * URL in it used to change with every ?variant= and made phones reload the
 * GLB on each colour change.
 */
(() => {
  if (customElements.get('wc-model-3d')) return;

  const cache = (window.__wc3d = window.__wc3d || { viewers: {}, ready: null, prefetched: false });

  // Mobile gate for AR: phones and tablets only. iPadOS Safari reports a
  // Mac user agent, so a touch-screen Mac counts as an iPad.
  const AR_DEVICE = (() => {
    const ua = navigator.userAgent || '';
    if (/Android|iPhone|iPad|iPod/i.test(ua)) return true;
    return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  })();
  const AR_LABEL = 'View in your room';

  const IS_IOS = /iPhone|iPad|iPod/i.test(navigator.userAgent || '') ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Back from AR, a line of copy over the theme's sticky add-to-cart bar
  // (sections/main-product.liquid <product-sticky-form>, which stays up while
  // the main buy button is off screen). It goes once the shopper adds to cart
  // or picks another variant.
  const arCartNote = {
    show() {
      const card = document.querySelector('product-sticky-form .product-sticky-form__card');
      if (!card) return;
      let note = card.querySelector('.wc3d-sticky-note');
      if (!note) {
        note = document.createElement('p');
        note.className = 'wc3d-sticky-note';
        note.textContent = 'Like how it looked in your room? Add it to your cart.';
        card.prepend(note);
      }
      note.hidden = false;
    },
    hide() {
      const note = document.querySelector('.wc3d-sticky-note');
      if (note) note.hidden = true;
    },
    get shown() {
      const note = document.querySelector('.wc3d-sticky-note');
      return !!note && !note.hidden;
    },
  };

  // Shopify's consent for analytics, from the Customer Privacy API (loaded on
  // demand, once). No API, or an error, counts as "no".
  let consentPromise = null;
  const analyticsAllowed = () => {
    const check = () => {
      try {
        return !!window.Shopify.customerPrivacy.analyticsProcessingAllowed();
      } catch (error) {
        return false;
      }
    };
    if (!consentPromise) {
      consentPromise = new Promise((resolve) => {
        const shopify = window.Shopify;
        if (shopify && shopify.customerPrivacy) return resolve(true);
        if (!shopify || !shopify.loadFeatures) return resolve(false);
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => resolve(!error));
      });
    }
    // re-read each time: the shopper may change their choice mid-visit
    return consentPromise.then((loaded) => loaded && check());
  };

  // Order attribution (3D_ATTRIBUTION_PLAN.md, Phase 1): which variants a
  // shopper rotated in 3D, opened in AR, or added to the cart while the
  // after-AR note was showing, kept as hidden cart attributes. The leading
  // underscore keeps them off the storefront; staff see them on the order
  // (Additional details) and in the orders export's "Note Attributes" column.
  // First-party only, SKUs only, only with analytics consent, never before
  // the shopper does something, and off with the theme setting "Log 3D and
  // AR use on orders" (no data-log). The cart itself is the store: each
  // write reads the cart's lists and adds one SKU, through a queue so two
  // quick writes cannot overwrite each other.
  //
  // The same moments also go out as Shopify custom events (Phase 3) for the
  // "Wall Control 3D -> GA4" custom pixel (analytics/ga4_custom_pixel.js).
  // Shopify hands them only to pixels the shopper's consent allows.
  const wc3dLog = {
    KEYS: { engaged: '_wc3d', ar: '_wc3d_ar', arcart: '_wc3d_arcart' },
    EVENTS: { engaged: 'wallcontrol:3d_rotate', ar: 'wallcontrol:ar_open', arcart: 'wallcontrol:ar_add_to_cart' },
    seen: new Set(),
    queue: Promise.resolve(),
    root() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    },
    cart() {
      return fetch(this.root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    },
    update(attributes) {
      return fetch(this.root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      });
    },
    publish(kind, detail) {
      try {
        if (window.Shopify && window.Shopify.analytics && window.Shopify.analytics.publish) {
          window.Shopify.analytics.publish(this.EVENTS[kind], detail);
        }
      } catch (error) {
        /* best effort */
      }
    },
    // kind: 'engaged' | 'ar' | 'arcart'; detail: { sku, variant_id,
    // product_id, price, currency }. Once per variant and kind per page.
    add(kind, detail, delay = 0) {
      const sku = detail && detail.sku;
      const tag = kind + ':' + sku;
      if (!sku || this.seen.has(tag)) return;
      this.seen.add(tag);
      this.publish(kind, detail);
      this.queue = this.queue
        .then(() => analyticsAllowed())
        .then((allowed) => {
          if (!allowed) return null;
          return new Promise((resolve) => setTimeout(resolve, delay))
            .then(() => this.cart())
            .then((cart) => {
              const key = this.KEYS[kind];
              const list = String((cart.attributes || {})[key] || '').split(',').filter(Boolean);
              if (list.includes(sku)) return null;
              list.push(sku);
              // attribute values stay short: keep the most recent SKUs
              while (list.length > 1 && list.join(',').length > 255) list.shift();
              return this.update({ [key]: list.join(','), _wc3d_v: '1' });
            });
        })
        .catch(() => {
          /* attribution is best effort; never in the shopper's way */
        });
    },
    // Consent withdrawn mid-visit: take this log back off the cart.
    clear() {
      this.queue = this.queue
        .then(() => this.cart())
        .then((cart) => {
          const attributes = cart.attributes || {};
          const keys = Object.values(this.KEYS).concat('_wc3d_v').filter((key) => attributes[key]);
          if (!keys.length) return null;
          return this.update(Object.fromEntries(keys.map((key) => [key, ''])));
        })
        .catch(() => {});
    },
  };
  document.addEventListener('visitorConsentCollected', (event) => {
    const detail = (event && event.detail) || {};
    if (detail.analyticsAllowed === false) wc3dLog.clear();
  });

  // Warm the HTTP cache with the viewer library (modulepreload) without
  // executing it. Once per page; a no-op after the library is loading.
  const prefetchModelViewer = (src) => {
    if (!src || cache.prefetched || cache.ready || customElements.get('model-viewer')) return;
    cache.prefetched = true;
    const link = document.createElement('link');
    link.rel = 'modulepreload';
    link.href = src;
    document.head.appendChild(link);
  };

  // Load and define <model-viewer>, pointing it at the vendored meshopt
  // decoder. Returns one shared promise per page, so every slide (and every
  // gallery re-render) waits on the same load.
  const loadModelViewer = (src, decoderSrc) => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!cache.ready) {
      cache.ready = new Promise((resolve, reject) => {
        if (!src) {
          reject(new Error('model-viewer asset URL missing'));
          return;
        }
        // Static config is read when the library defines the element.
        if (decoderSrc && !window.ModelViewerElement) {
          window.ModelViewerElement = { meshoptDecoderLocation: decoderSrc };
        }
        const script = document.createElement('script');
        script.type = 'module';
        script.src = src;
        script.onload = resolve;
        script.onerror = () => reject(new Error('model-viewer failed to load'));
        document.head.appendChild(script);
      }).then(() => customElements.whenDefined('model-viewer'));
    }
    return cache.ready;
  };

  // '#rgb' / '#rrggbb' -> linear-light [r, g, b] in 0..1, which is what
  // glTF base colour factors expect.
  const srgbToLinear = (c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const hexToLinear = (hex) => {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    const n = parseInt(h, 16);
    return [16, 8, 0].map((shift) => srgbToLinear(((n >> shift) & 255) / 255));
  };

  // Material recipes: powder coat for hex swatches, brushed steel for the
  // gradient swatches (Metallic / Galvanized). The static posters are
  // rendered with the same recipes - keep the two in step.
  const METALLIC = { hex: '#a9aaad', metallic: 0.9, roughness: 0.32 };
  const FALLBACK = { hex: '#8d8f92', metallic: 0, roughness: 0.45 };
  // Finishes whose 3D look must not follow the UI swatch hex. The Gold
  // powder coat is a muted satin-metallic champagne in the listing photos
  // (sampled ~#b9a56c), not the saturated #c69b30 the swatch uses.
  // Beige and Gray: the swatch hexes render too yellow / too light
  // and green; these are solved so the rendered panel face matches the
  // listing photos' median (beige 234,227,216; gray 184,184,184).
  const FINISH_3D = {
    gold: { hex: '#ad9760', metallic: 0.35, roughness: 0.5 },
    beige: { hex: '#d1cbc1', metallic: 0, roughness: 0.45 },
    gray: { hex: '#a3a3a3', metallic: 0, roughness: 0.45 },
    grey: { hex: '#a3a3a3', metallic: 0, roughness: 0.45 },
  };
  // Studio backdrop baked into snapshots (same as product-3d.css and the
  // static posters): centre -> farthest corner.
  const BACKDROP = ['#ebebeb', '#d0d0d0'];
  const paintBackdrop = (ctx, w, h) => {
    const g = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.hypot(w / 2, h / 2));
    g.addColorStop(0, BACKDROP[0]);
    g.addColorStop(1, BACKDROP[1]);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  };

  // Material recipe { hex, metallic, roughness } for a colour option value.
  // `map` is the lower-cased swatch_config (name -> hex or gradient CSS).
  // Order: FINISH_3D override, brushed steel, swatch hex, neutral fallback.
  const recipeFor = (name, map) => {
    const key = (name || '').trim().toLowerCase();
    const value = map[key] || '';
    if (FINISH_3D[key]) return FINISH_3D[key];
    if (/metal|galvan/.test(key) || /gradient/.test(value)) return METALLIC;
    const hex = value.match(/#[0-9a-f]{6}|#[0-9a-f]{3}/i);
    if (!hex) return FALLBACK;
    return { hex: hex[0], metallic: 0, roughness: 0.45 };
  };

  // data-* contract: see the <wc-model-3d> tag in product-3d-media.liquid.
  class WcModel3d extends HTMLElement {
    // Reads the data-* config, listens for variant:change (recolour, and
    // swap the model on listings whose option picks a different part), then
    // either re-adopts the cached viewer or waits for intent.
    connectedCallback() {
      this.key = this.dataset.key;
      this.inLightbox = !!this.closest('.pswp');
      try {
        this.colorMap = JSON.parse(this.dataset.colorMap || '{}');
      } catch (error) {
        this.colorMap = {};
      }
      this.color = this.dataset.color || '';
      this.accessory = this.dataset.accessory || '';
      this.form = document.forms[this.dataset.form] || null;

      try {
        this.sizeMap = JSON.parse(this.dataset.sizeMap || '{}');
      } catch (error) {
        this.sizeMap = {};
      }
      this.onVariantChange = (event) => {
        const variant = event.detail && event.detail.variant;
        const name = variant && this.dataset.colorOption && variant[this.dataset.colorOption];
        const accessory = variant && this.dataset.accessoryOption && variant[this.dataset.accessoryOption];
        if (accessory) this.accessory = accessory;
        if (name) this.setColor(name);
        else if (accessory) this.applyColor();
        // listings whose Size option is a different part (the S-bracket sizes)
        const size = variant && this.dataset.sizeOption && variant[this.dataset.sizeOption];
        const src = size && this.sizeMap[size];
        if (src && this.viewer && this.viewer.getAttribute('src') !== src) {
          if (this.entry) this.entry.loaded = false;
          this.viewer.setAttribute('src', src);
        }
        arCartNote.hide();
      };
      (this.form || document).addEventListener('variant:change', this.onVariantChange);

      // Already booted earlier on this page (gallery re-render): re-adopt now.
      if (this.entry && this.entry.viewer) {
        this.boot();
        return;
      }
      this.arm();
    }

    disconnectedCallback() {
      (this.form || document).removeEventListener('variant:change', this.onVariantChange);
      this.disarm();
      if (this.observer) this.observer.disconnect();
      if (this.viewer) {
        this.viewer.removeEventListener('load', this.onLoad);
        this.viewer.removeEventListener('camera-change', this.onCamera);
        this.viewer.removeEventListener('ar-status', this.onArStatus);
        this.viewer.removeEventListener('wc3d-quick-look', this.onQuickLookLaunch);
      }
      (this.snapTimers || []).forEach(clearTimeout);
    }

    get slide() {
      return this.closest('.product__media');
    }

    get gallery() {
      return this.closest('media-gallery');
    }

    get thumbnails() {
      const gallery = this.gallery;
      return gallery ? Array.from(gallery.querySelectorAll('.product__thumbnail[data-media-id="' + this.key + '"]')) : [];
    }

    // This slide's cache record { viewer, active, loaded }. Lightbox clones
    // never share the cached viewer.
    get entry() {
      return this.inLightbox ? null : cache.viewers[this.key];
    }

    caption(text) {
      const node = this.querySelector('.wc3d-caption');
      if (node) node.textContent = text;
    }

    // Once the shopper is back from AR: the sticky bar's note. Nothing for a
    // variant that cannot be bought (no data-buyable). Adding to cart (any
    // button of this form) retires it.
    offerAfterAr() {
      if (!this.hasAttribute('data-buyable')) return;
      arCartNote.show();
      if (!this.form) return;
      const detail = this.logDetail();
      this.form.addEventListener('submit', () => {
        // logged once the theme's add has had time to land, so the attribute
        // write never races the request that creates the cart
        if (detail && arCartNote.shown) wc3dLog.add('arcart', detail, 2500);
        arCartNote.hide();
      }, { once: true });
    }

    // What wc3dLog records for this slide's variant, or null when logging is
    // off (theme setting "Log 3D and AR use on orders": no data-log).
    logDetail() {
      if (!this.hasAttribute('data-log')) return null;
      return {
        sku: this.dataset.sku,
        variant_id: this.dataset.variantId || '',
        product_id: (this.dataset.key || '').replace('wc3d-', ''),
        price: parseFloat(this.dataset.price) || 0,
        currency: this.dataset.currency || '',
      };
    }

    // Order attribution + GA4 event for this slide's variant (wc3dLog).
    log(kind) {
      const detail = this.logDetail();
      if (detail) wc3dLog.add(kind, detail);
    }

    // Wait for intent before fetching anything.
    arm() {
      const slide = this.slide || this;
      const gallery = this.gallery;
      const viewerSrc = this.dataset.viewerSrc;

      this.onIntent = () => this.boot();
      this.onWarm = () => prefetchModelViewer(viewerSrc);
      this.armed = [];
      const listen = (target, type, handler, options) => {
        if (!target) return;
        target.addEventListener(type, handler, options);
        this.armed.push([target, type, handler, options]);
      };

      listen(slide, 'pointerdown', this.onIntent, { passive: true });
      listen(slide, 'touchstart', this.onIntent, { passive: true });
      this.thumbnails.forEach((thumb) => {
        listen(thumb, 'click', this.onIntent);
        listen(thumb, 'pointerenter', this.onWarm, { passive: true });
      });
      if (gallery) {
        listen(gallery, 'pointerdown', this.onWarm, { passive: true, capture: true });
        listen(gallery, 'touchstart', this.onWarm, { passive: true, capture: true });
      }

      if ('IntersectionObserver' in window) {
        // A slide parked exactly at the slider's edge "intersects" with zero
        // area at threshold 0 (that is where the next slide sits on desktop),
        // so only a positive overlap counts.
        this.intentObserver = new IntersectionObserver((entries) => {
          if (entries.some((e) => e.isIntersecting && e.intersectionRatio > 0)) this.boot();
        }, { threshold: [0, 0.01] });
        this.intentObserver.observe(slide);
      } else {
        this.boot();
      }
    }

    // Drop every intent listener and the viewport observer set by arm().
    disarm() {
      (this.armed || []).forEach(([target, type, handler, options]) => target.removeEventListener(type, handler, options));
      this.armed = [];
      if (this.intentObserver) {
        this.intentObserver.disconnect();
        this.intentObserver = null;
      }
    }

    // Once per element: load the library, then mount. A failed load leaves
    // the static poster in place with an "unavailable" caption.
    boot() {
      if (this.booted) return;
      this.booted = true;
      this.disarm();
      this.classList.add('wc3d--booting');
      loadModelViewer(this.dataset.viewerSrc, this.dataset.decoderSrc)
        .then(() => this.mount())
        .catch((error) => {
          this.classList.add('wc3d--failed');
          this.caption('3D preview unavailable');
          console.warn('[wc-model-3d]', error);
        });
    }

    // Create the <model-viewer> (or re-adopt the cached one, swapping its
    // src if this variant maps to a different part), wire its events and
    // attach it to this slide.
    mount() {
      if (!this.isConnected) return;

      let viewer = this.entry ? this.entry.viewer : null;
      if (!viewer) {
        viewer = document.createElement('model-viewer');
        viewer.setAttribute('src', this.dataset.src);
        viewer.setAttribute('alt', this.dataset.alt || '3D model');
        if (this.dataset.poster) viewer.setAttribute('poster', this.dataset.poster);
        viewer.setAttribute('camera-controls', '');
        viewer.setAttribute('touch-action', 'pan-y');
        viewer.setAttribute('loading', 'eager');
        viewer.setAttribute('shadow-intensity', '1');
        viewer.setAttribute('shadow-softness', '0.8');
        viewer.setAttribute('exposure', '1');
        viewer.setAttribute('interaction-prompt', 'auto');
        viewer.setAttribute('camera-orbit', this.dataset.orbit || '0deg 75deg auto');
        if (AR_DEVICE && this.dataset.ar === 'wall') {
          viewer.setAttribute('ar', '');
          viewer.setAttribute('ar-modes', 'webxr quick-look');
          viewer.setAttribute('ar-placement', 'wall');
          viewer.setAttribute('ar-scale', 'fixed');
          const button = document.createElement('button');
          button.type = 'button';
          button.slot = 'ar-button';
          button.className = 'button button--sm wc3d-ar';
          button.textContent = AR_LABEL;
          button.setAttribute('aria-label', AR_LABEL + ', opens augmented reality');
          viewer.appendChild(button);
          // Quick Look reports nothing back (no ar-status), but the AR button
          // calls viewer.activateAR(), which resolves once Quick Look has
          // been launched: announce that to whichever slide owns the viewer.
          if (IS_IOS) {
            const activate = viewer.activateAR.bind(viewer);
            viewer.activateAR = async () => {
              await activate();
              viewer.dispatchEvent(new CustomEvent('wc3d-quick-look'));
            };
          }
        }
        if (!this.inLightbox) cache.viewers[this.key] = { viewer, active: false, loaded: false };
      } else if (this.dataset.src && viewer.getAttribute('src') !== this.dataset.src) {
        // Re-rendered for a variant that maps to a different part (the
        // Size / Depth listings): Liquid already baked the new file in.
        if (this.entry) this.entry.loaded = false;
        viewer.setAttribute('src', this.dataset.src);
        if (this.dataset.poster) viewer.setAttribute('poster', this.dataset.poster);
      }
      this.viewer = viewer;

      this.onLoad = () => {
        if (this.entry) this.entry.loaded = true;
        this.classList.remove('wc3d--booting');
        this.classList.add('wc3d--loaded');
        this.applyColor();
        this.syncAr();
      };
      this.onCamera = (event) => {
        if (event.detail && event.detail.source === 'user-interaction') {
          this.classList.add('wc3d--interacted');
          this.log('engaged');
        }
      };
      // AR session lifecycle: 'session-started' / 'object-placed' /
      // 'not-presenting' / 'failed' (WebXR refused, or Quick Look could not
      // be launched).
      this.onArStatus = (event) => {
        const status = event.detail && event.detail.status;
        this.syncAr();
        if (status === 'failed') {
          this.classList.add('wc3d--ar-failed');
          this.caption('AR could not start on this device');
          arCartNote.hide();
        } else if (status === 'session-started') {
          this.classList.remove('wc3d--ar-failed');
          this.inAr = true;
          this.log('ar');
        } else if (status === 'not-presenting' && this.inAr) {
          // WebXR (Android) session over: the shopper is back on the page
          this.inAr = false;
          this.offerAfterAr();
        }
      };
      viewer.addEventListener('load', this.onLoad);
      viewer.addEventListener('camera-change', this.onCamera);
      viewer.addEventListener('ar-status', this.onArStatus);
      // Quick Look now covers the page and never says when it closes, so the
      // note is put in place underneath, ready for the shopper's return.
      this.onQuickLookLaunch = () => {
        this.log('ar');
        setTimeout(() => this.offerAfterAr(), 800);
      };
      viewer.addEventListener('wc3d-quick-look', this.onQuickLookLaunch);

      this.appendChild(viewer);
      // A re-adopted viewer may have finished loading while no slide was
      // listening (gallery re-render mid-load): trust its own flag too.
      if ((this.entry && this.entry.loaded) || viewer.loaded) {
        this.classList.remove('wc3d--booting');
        this.classList.add('wc3d--loaded');
        this.applyColor();
        this.syncAr();
      }
      if (!this.inLightbox) this.track();
    }

    // Remember whether the shopper was looking at the 3D slide so a variant
    // change (which rebuilds the gallery and jumps to the variant photo)
    // brings them back to the recoloured model.
    track() {
      const slide = this.slide;
      const slider = slide && slide.closest('slider-element');
      if (!slide || !slider || !this.entry) return;

      if (this.entry.active) {
        setTimeout(() => {
          if (this.isConnected) slide.scrollIntoView({ block: 'nearest', inline: 'start', behavior: 'instant' });
        }, 180);
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const active = e.intersectionRatio >= 0.6;
          if (this.entry) this.entry.active = active;
          // model-viewer only draws while on screen, so the first usable
          // snapshot is available once the shopper reaches this slide.
          if (active) this.snapshot();
        });
      }, { root: slider, threshold: [0.6] });
      this.observer.observe(slide);
    }

    setColor(name) {
      this.color = name;
      this.applyColor();
    }

    // model-viewer decides asynchronously whether this device can start AR
    // and only then shows the slotted button; mirror that as a class so the
    // caption can make room for it (product-3d.css).
    syncAr() {
      if (this.viewer && this.viewer.canActivateAR) this.classList.add('wc3d--ar');
    }

    // Paint the current colour's recipe onto every material except the
    // "WC-Fixed-*" ones, then refresh the thumbnail snapshot. The static
    // posters are painted the same way - keep the two in step.
    applyColor() {
      const model = this.viewer && this.viewer.model;
      if (!model || !this.color) return;
      const recipe = recipeFor(this.color, this.colorMap);
      const [r, g, b] = hexToLinear(recipe.hex);
      // kits: steel accessories and bins follow the Accessory Color option
      const accessoryName = this.accessory || this.color;
      // the rod's end caps belong to the accessories (kits with an Accessory Color)
      const capWhite = (accessoryName || '').trim().toLowerCase() === 'white';
      const recipe2 = recipeFor(accessoryName, this.colorMap);
      const [r2, g2, b2] = hexToLinear(recipe2.hex);
      const binsClear = (accessoryName || '').trim().toLowerCase() === 'white';
      model.materials.forEach((material) => {
        const pbr = material.pbrMetallicRoughness;
        const name = material.name || '';
        // composed kits carry parts that are not powder-coated as "WC-Fixed-*"
        // materials: the rubber end caps are white when the accessory colour
        // is White and black otherwise; the wooden dowel / rod is left alone
        if (/^WC-Fixed-Cap/.test(name)) {
          pbr.setBaseColorFactor(capWhite ? [0.9, 0.9, 0.9, 1] : [0.012, 0.012, 0.013, 1]);
          pbr.setMetallicFactor(0);
          pbr.setRoughnessFactor(0.5);
          return;
        }
        if (/^WC-Fixed/.test(name)) return;
        if (/^WC-Bin/.test(name)) {
          // plastic bins: the accessory colour, or clear/translucent on White
          if (binsClear) {
            pbr.setBaseColorFactor([0.92, 0.95, 0.97, 0.42]);
            pbr.setMetallicFactor(0);
            pbr.setRoughnessFactor(0.12);
            if (material.setAlphaMode) material.setAlphaMode('BLEND');
          } else {
            pbr.setBaseColorFactor([r2, g2, b2, 1]);
            pbr.setMetallicFactor(0);
            pbr.setRoughnessFactor(0.4);
            if (material.setAlphaMode) material.setAlphaMode('OPAQUE');
          }
          return;
        }
        if (/^WC-PowderCoat-Accessory/.test(name)) {
          pbr.setBaseColorFactor([r2, g2, b2, 1]);
          pbr.setMetallicFactor(recipe2.metallic);
          pbr.setRoughnessFactor(recipe2.roughness);
          return;
        }
        pbr.setBaseColorFactor([r, g, b, 1]);
        pbr.setMetallicFactor(recipe.metallic);
        pbr.setRoughnessFactor(recipe.roughness);
      });
      this.snapshot();
    }

    // Mirror the live view into the gallery thumbnail + lightbox poster.
    // model-viewer only redraws once the slide is on screen, so after a
    // gallery rebuild the first capture can still show the previous colour:
    // capture a few times and let the last good frame win.
    snapshot() {
      (this.snapTimers || []).forEach(clearTimeout);
      this.snapTimers = [500, 1400, 3000].map((delay) => setTimeout(() => this.capture(), delay));
    }

    // One snapshot attempt: grab the viewer frame, skip it if blank, compose
    // it over the studio backdrop and write it into the poster + thumbnails.
    capture() {
      if (!this.viewer || !this.isConnected) return;
      try {
        const url = this.viewer.toDataURL('image/png');
        if (!url) return;
        // An off-screen viewer hands back a fully transparent frame: probe a
        // tiny downscale for any opaque pixel before using the capture.
        const probe = new Image();
        probe.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 24;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(probe, 0, 0, 24, 24);
          const data = ctx.getImageData(0, 0, 24, 24).data;
          let opaque = 0;
          for (let i = 3; i < data.length; i += 4) if (data[i] > 64) opaque++;
          if (opaque < 4 || !this.isConnected) return;
          const out = document.createElement('canvas');
          out.width = probe.naturalWidth;
          out.height = probe.naturalHeight;
          const octx = out.getContext('2d');
          paintBackdrop(octx, out.width, out.height);
          octx.drawImage(probe, 0, 0);
          const composed = out.toDataURL('image/jpeg', 0.9);
          const targets = [];
          const poster = this.slide && this.slide.querySelector('img.wc3d-poster');
          if (poster) targets.push(poster);
          this.thumbnails.forEach((thumb) => targets.push(...thumb.querySelectorAll('img')));
          targets.forEach((img) => {
            img.removeAttribute('srcset');
            img.src = composed;
          });
        };
        probe.src = url;
      } catch (error) {
        /* the snapshot is cosmetic */
      }
    }

    // The theme's media gallery calls play()/pause() on every .deferred-media
    // when the active slide changes. Nothing to start or stop here (no
    // auto-rotate: a spinning flat panel reads as an error), but the methods
    // must exist.
    play() {}

    pause() {}
  }

  customElements.define('wc-model-3d', WcModel3d);
})();
